import { useEffect } from "react"

import { useMatchRoute, useNavigate, useSearch } from "@tanstack/react-router"
import { getHasAdminSessionParam } from "src/domains/http/interceptors/use-admin-session-interceptor"

import { TFetchSessionsResponse } from "./session-types"

type TSession = TFetchSessionsResponse["sessions"][number]

// Search param that suppresses the redirect to `/account-redirect`, so an already
// authenticated user can reach the login form to sign in with a different account.
export const SKIP_APP_SELECT_PARAM = "skip_app_select"

// Auth-form routes an already-authenticated user is redirected away from,
// toward the app-select screen.
const GUARDED_ROUTES = [
  { to: "/login" },
  { to: "/signup" },
  { to: "/password/change" },
  { to: "/password/reset" },
] as const

export function useRedirectToAppSelect(sessions: TSession[]) {
  const navigate = useNavigate()
  const matchRoute = useMatchRoute()

  const wantsAdminSession = getHasAdminSessionParam()

  const { [SKIP_APP_SELECT_PARAM]: skipSessionSelect } = useSearch({
    strict: false,
  })

  const isGuardedRoute = GUARDED_ROUTES.some((route) =>
    Boolean(matchRoute(route))
  )

  const hasAdminSession = sessions.some((session) => session.is_admin)

  // The URL asks for an admin session but the user only holds a personal one:
  // keep them on the auth form to sign in as admin instead of selecting.
  const awaitingAdminSignIn = wantsAdminSession && !hasAdminSession

  const shouldSelectSession =
    isGuardedRoute &&
    sessions.length > 0 &&
    !awaitingAdminSignIn &&
    !skipSessionSelect

  useEffect(() => {
    if (shouldSelectSession) {
      void navigate({ to: "/app-select" })
    }
  }, [shouldSelectSession, navigate])

  return null
}
